// utils/jikan.js
import { sample, PLACEHOLDER } from "./placeholders";

// Jikan API base URL - direct call (Jikan supports CORS)
const JIKAN_BASE = "https://api.jikan.moe/v4";

// Cache for Jikan data
let jikanCache = {};
const JIKAN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

function getJikanCache(key) {
  const cached = jikanCache[key];
  if (cached && Date.now() - cached.timestamp < JIKAN_CACHE_DURATION) {
    return cached.data;
  }
  return null;
}

function setJikanCache(key, data) {
  jikanCache[key] = { data, timestamp: Date.now() };
}

// Direct fetch to Jikan API (bypassing proxy)
async function fetchJikanDirect(endpoint, query = {}) {
  const queryString = new URLSearchParams(query).toString();
  const url = queryString 
    ? `${JIKAN_BASE}${endpoint}?${queryString}` 
    : `${JIKAN_BASE}${endpoint}`;
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);
  
  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'User-Agent': 'Animac-Metaverse/1.0',
      },
    });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`Jikan API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (err) {
    clearTimeout(timeoutId);
    console.error("Jikan fetch error:", err);
    throw err;
  }
}

function mapJikanAnime(a) {
  const title = a.title_english || a.title || "Untitled";
  const youtubeId = a.trailer?.youtube_id;

  return {
    id: `east-jikan-${a.mal_id}`,
    title,
    year: a.year || a.aired?.prop?.from?.year || "—",
    rating: typeof a.score === "number" ? +a.score.toFixed(1) : 0,
    poster: a.images?.jpg?.large_image_url || a.images?.jpg?.image_url || sample(PLACEHOLDER.posters),
    backdrop: a.trailer?.images?.maximum_image_url || sample(PLACEHOLDER.backdrops),
    type: "anime",
    region: "east",
    synopsis: a.synopsis || "",
    trailerUrl: youtubeId ? `https://www.youtube.com/watch?v=${youtubeId}` : a.trailer?.url || null,
    _meta: { source: "jikan", malId: a.mal_id },
  };
}

export async function fetchJikanTrending() {
  const cacheKey = 'trending';
  const cached = getJikanCache(cacheKey);
  if (cached) return cached;
  
  try {
    const json = await fetchJikanDirect(`/top/anime`, { limit: 20 });
    const data = (json?.data || []).map(mapJikanAnime);
    setJikanCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error("Jikan trending error:", err);
    return [];
  }
}

export async function fetchJikanUpcoming() {
  const cacheKey = 'upcoming';
  const cached = getJikanCache(cacheKey);
  if (cached) return cached;
  
  try {
    const json = await fetchJikanDirect(`/seasons/upcoming`, { limit: 24 });
    const data = (json?.data || []).map(mapJikanAnime);
    setJikanCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error("Jikan upcoming error:", err);
    return []; // Return empty array instead of throwing
  }
}

export async function fetchJikanTopRated() {
  const cacheKey = 'toprated';
  const cached = getJikanCache(cacheKey);
  if (cached) return cached;
  
  try {
    const json = await fetchJikanDirect(`/top/anime`, { limit: 24 });
    const data = (json?.data || []).map(mapJikanAnime);
    setJikanCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error("Jikan toprated error:", err);
    return [];
  }
}

// ✅ Real recommendations per anime
export async function fetchJikanRecommended(malId) {
  const cacheKey = `recommendations-${malId}`;
  const cached = getJikanCache(cacheKey);
  if (cached) return cached;
  
  try {
    const json = await fetchJikanDirect(`/anime/${malId}/recommendations`);
    const data = (json?.data || [])
      .map(r => r.entry)
      .filter(Boolean)
      .map(mapJikanAnime);
    setJikanCache(cacheKey, data);
    return data;
  } catch (err) {
    console.error("Jikan recommendations error:", err);
    return [];
  }
}
